import { useState } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, TextInput } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';

/**
 * Screen 17: read-only links for people outside Maestro (rule 13).
 *
 * The token is shown once, here, and never again: only its hash is stored. The
 * page says so rather than leaving somebody hunting for it later.
 *
 * Thumbnails are on by default and everything else is off, so forgetting to
 * think about it is the private answer rather than the open one. There is no
 * link that never expires.
 */

interface ShareRow {
  _id: string;
  subject: { kind: string; id: string };
  show: { thumbs: boolean; notes: boolean; owner: boolean; where: boolean };
  audience: 'anyone' | 'signed_in';
  expires_at: string;
  created_by: string;
  views?: number;
  state: 'live' | 'expired' | 'turned_off';
  says: string;
  can_turn_back_on: boolean;
  shows: string[];
}

interface Choices {
  kinds: string[];
  expiry: { hours: number; says: string }[];
  about_expiry: string;
  show_defaults: { thumbs: boolean; notes: boolean; owner: boolean; where: boolean };
  what_each_tick_hides: Record<string, string>;
  audiences: { key: string; says: string }[];
}

export function ShareLinks({ projectId }: { projectId: string }) {
  const [toast, setToast] = useState<string | null>(null);
  const [made, setMade] = useState<{ url: string; absolute: boolean } | null>(null);
  const [making, setMaking] = useState<{
    kind: string;
    id: string;
    hours: number;
    show: Choices['show_defaults'];
    audience: 'anyone' | 'signed_in';
  } | null>(null);

  const { state: choices } = useLoad<Choices>(() => api.get('/api/share/choices'), []);
  const { state, reload } = useLoad<{ links: ShareRow[]; counts: Record<string, number> }>(
    () => api.get(`/api/share?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );
  const links = state.data?.links ?? [];

  const create = async (): Promise<void> => {
    if (!making) return;
    try {
      const result = await api.post<{ url: string; absolute: boolean }>('/api/share', {
        project_id: projectId,
        kind: making.kind,
        id: making.id.trim(),
        hours: making.hours,
        show: making.show,
        audience: making.audience
      });
      setMaking(null);
      setMade(result);
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That link could not be made.');
    }
  };

  const flip = async (link: ShareRow, on: boolean): Promise<void> => {
    try {
      await api.post(`/api/share/${link._id}/${on ? 'on' : 'off'}`);
      setToast(on ? 'Turned back on.' : 'Turned off. Anybody holding it now gets nothing.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That did not work.');
    }
  };

  const defaults = choices.data?.show_defaults ?? { thumbs: true, notes: false, owner: false, where: false };

  return (
    <Page
      eyebrow="MAESTRO · SHARE LINKS"
      title="Share links"
      description="Read-only links for people outside Maestro. The page they see has no way into the rest of it."
      note={choices.data?.about_expiry}
      action={
        <Button
          tone="primary"
          onClick={() =>
            setMaking({ kind: 'take', id: '', hours: 24, show: defaults, audience: 'anyone' })
          }
        >
          Make a link
        </Button>
      }
    >
      {state.status === 'loading' && links.length === 0 && <Loading says="Loading the links…" rows={3} />}
      {state.status === 'failed' && links.length === 0 && (
        <CouldNotLoad what="the share links" error={state.error} onRetry={reload} />
      )}
      {state.status !== 'loading' && links.length === 0 && state.status !== 'failed' && (
        <Empty
          title="No share links yet"
          says="Make one for a take, a VC pass or an edit. Thumbnails are shown by default; notes, names and file locations are not."
        />
      )}

      {links.map((link) => (
        <Card key={link._id}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
            <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.bodyStrong}>{link.subject.kind.replace('_', ' ')}</span>
                <Status
                  value={link.state === 'live' ? 'good' : link.state === 'expired' ? 'superseded' : 'bad'}
                  detail={link.says}
                />
              </span>
              <span style={{ ...text.note, color: colour.inkSoft }}>Shows: {link.shows.join(', ')}</span>
              <span style={{ ...text.note, color: colour.inkMuted }}>
                {link.audience === 'signed_in' ? 'Only people signed in to Maestro' : 'Anyone with the link'} ·
                made by {link.created_by} · {link.views ?? 0} views
              </span>
            </div>
            {link.state === 'live' && (
              <Button tone="danger" onClick={() => void flip(link, false)}>
                Turn off
              </Button>
            )}
            {link.can_turn_back_on && <Button onClick={() => void flip(link, true)}>Turn back on</Button>}
          </div>
        </Card>
      ))}

      {making && choices.data && (
        <Dialog
          title="Make a share link"
          says="Everything is off except thumbnails, so forgetting to think about it is the private answer."
          confirm="Make it"
          onConfirm={() => void create()}
          onClose={() => setMaking(null)}
        >
          <Field label="What to share">
            <Select
              value={making.kind}
              onChange={(kind) => setMaking({ ...making, kind })}
              options={choices.data.kinds.map((k) => ({ value: k, label: k.replace('_', ' ') }))}
            />
          </Field>
          <Field label="Its id">
            <TextInput value={making.id} onChange={(id) => setMaking({ ...making, id })} />
          </Field>
          <Field label="How long it lasts">
            <Select
              value={String(making.hours)}
              onChange={(h) => setMaking({ ...making, hours: Number(h) })}
              options={choices.data.expiry.map((e) => ({ value: String(e.hours), label: e.says }))}
            />
          </Field>
          <Field label="Who can open it">
            <Select
              value={making.audience}
              onChange={(audience) => setMaking({ ...making, audience: audience as 'anyone' | 'signed_in' })}
              options={choices.data.audiences.map((a) => ({ value: a.key, label: a.says }))}
            />
          </Field>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(['thumbs', 'notes', 'owner', 'where'] as const).map((key) => (
              <label
                key={key}
                style={{ display: 'flex', alignItems: 'center', gap: space.tight, ...text.small }}
              >
                <input
                  type="checkbox"
                  checked={making.show[key]}
                  onChange={(e) => setMaking({ ...making, show: { ...making.show, [key]: e.target.checked } })}
                  style={{ width: 20, height: 20 }}
                />
                {choices.data!.what_each_tick_hides[key] ?? key}
              </label>
            ))}
          </div>
        </Dialog>
      )}

      {made && (
        <Dialog
          title="Copy this link now"
          says="Maestro stores only a hash of it, so it cannot be shown again. If you lose it, make another."
          confirm="Done"
          onConfirm={() => setMade(null)}
          onClose={() => setMade(null)}
        >
          <code
            style={{
              padding: 10,
              borderRadius: radius.control,
              background: colour.surfaceSunk,
              border: `1px solid ${colour.edgeSoft}`,
              ...text.mono,
              wordBreak: 'break-all'
            }}
          >
            {made.absolute ? made.url : `${window.location.origin}${made.url}`}
          </code>
          <Button
            onClick={() =>
              void navigator.clipboard?.writeText(
                made.absolute ? made.url : `${window.location.origin}${made.url}`
              )
            }
          >
            Copy
          </Button>
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
